import React from 'react';

import FeatureBadge from '~/components/seed/feature-badge';
import { useFormAnswers } from '~/hooks/use-form-answers';

import styles from '~/components/plans/bundles-page/smart-badges.module.scss';

const BadgeContent = {
  LOCAL_ORDINANCES: {
    icon: {
      src: '/icons/psp-badges/local-ordinances.svg',
      alt: 'A location marker with a checkmark',
    },
    text: 'Local rules',
    tooltipText: `Your custom products and schedule respect local water protection laws while maximizing your lawn's growth potential during approved months.`,
  },
  WORRY_FREE_PLAY: {
    icon: {
      src: '/icons/seed-finder/paw.svg',
      alt: 'Animal paw',
    },
    text: 'Play proof',
    tooltipText:
      'Tough enough for active yards, safe enough for peace of mind when used as directed. Your plan builds resilient turf that bounces back from play.',
  },
  PLANET_FRIENDLY: {
    icon: {
      src: '/icons/psp-badges/planet.svg',
      alt: 'A hand holding the Earth',
    },
    text: 'Planet-friendly',
    tooltipText: `Your plan proves great lawns don't need harsh chemicals or irresponsible ingredients. Every product works with nature, not against it.`,
  },
  COOL_SEASON: {
    icon: {
      src: '/icons/seed-finder/cold-tolerant.svg',
      alt: 'Snowflake',
    },
    text: 'Cool zone',
    tooltipText:
      'Your nutrients arrive exactly when your cool season grass needs them—from spring green-up through fall recovery.',
  },
  WARM_SEASON: {
    icon: {
      src: '/icons/seed-finder/southern-staple.svg',
      alt: 'Sun',
    },
    text: 'Warm zone',
    tooltipText:
      'Your nutrients arrive exactly when your warm season grass needs them—from spring wake-up through fall slowdown.',
  },
  EASY_PRECISION: {
    icon: {
      src: '/icons/psp-badges/easy-precision.svg',
      alt: 'Spraying water',
    },
    text: 'Hose easy',
    tooltipText:
      'Quick, precise, custom nutrients exactly where and when your lawn needs it. Just connect your hose for perfect coverage.',
  },
  EASY_APPLY: {
    icon: {
      src: '/icons/psp-badges/easy-apply.svg',
      alt: 'A seed spreader',
    },
    text: 'Easy spread',
    tooltipText:
      'Just grab a spreader for fewer applications, lasting results. Your slow-release granules feed consistently all season for a green, lush lawn.',
  },
};
const SmartBadges = ({ lawn, fertilizerType, selectedBundleTitle }) => {
  const { formAnswers } = useFormAnswers();
  const lawnFormAnswers = formAnswers?.LAWN_FUNNEL?.[0]?.answers;

  const lawnUseQuestion = lawnFormAnswers?.find(
    (question) => question.question === 'lawn-use'
  );
  const importanceQuestion = lawnFormAnswers?.find(
    (question) => question.question === 'lawn-importance'
  );

  const badges = [];

  if (['NY', 'FL', 'MD', 'VT', 'NJ'].includes(lawn.address.stateCode)) {
    badges.push(BadgeContent.LOCAL_ORDINANCES);
  }

  // We only want the user to see ONE of these two badges, so use if-else
  if (
    lawnUseQuestion?.response === 'use-pets' ||
    selectedBundleTitle === 'Lawn & Paws'
  ) {
    badges.push(BadgeContent.WORRY_FREE_PLAY);
  } else if (importanceQuestion?.response === 'importance-safe') {
    badges.push(BadgeContent.PLANET_FRIENDLY);
  }

  badges.push(
    BadgeContent?.[`${lawn?.climate.grassType.toUpperCase()}_SEASON`]
  );

  if (fertilizerType === 'liquid') {
    badges.push(BadgeContent.EASY_PRECISION);
  } else {
    badges.push(BadgeContent.EASY_APPLY);
  }

  return (
    <div className={styles.badgeContainer}>
      {/* Only display the first two badges => use .slice(0,2) */}
      {badges.slice(0, 2).map((badge) => (
        <FeatureBadge
          text={badge.text}
          tooltipText={badge.tooltipText}
          icon={badge.icon}
          key={badge.text}
        />
      ))}
    </div>
  );
};

export default SmartBadges;
